/**
 * compositionRegistry.ts — Registro central das composições do Studio
 *
 * Faz três coisas:
 *  1. guarda as composições disponíveis;
 *  2. EXPANDE a composição em árvore normal de blocos na hora de inserir,
 *     carimbando o espaço semântico de cada peça e a origem da composição;
 *  3. confere, na publicação, se algum espaço obrigatório ficou vazio.
 *
 * A composição nunca é consultada para desenhar a página: depois de inserida,
 * o documento se sustenta sozinho.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (Studio, motor de composições — Fase 8)
 */

import { getBlock } from '../blockRegistry';
import type { StudioBlockNode, StudioOutput, StudioTemplateType } from '../types';
import type {
  StudioCompositionDefinition,
  StudioCompositionFamily,
  StudioCompositionNode,
  StudioCompositionSlot,
} from './types';

const composicoes = new Map<string, StudioCompositionDefinition>();

/** Registra (ou substitui) uma composição. */
export function registerComposition(definicao: StudioCompositionDefinition): void {
  composicoes.set(definicao.key, definicao);
}

/** Registra várias composições de uma vez. */
export function registerCompositions(lista: StudioCompositionDefinition[]): void {
  lista.forEach(registerComposition);
}

/** Retorna uma composição pela chave. */
export function getComposition(key: string): StudioCompositionDefinition | undefined {
  return composicoes.get(key);
}

/** Filtros aceitos ao listar composições. */
export interface StudioCompositionFilters {
  family?: StudioCompositionFamily;
  templateType?: StudioTemplateType;
  outputs?: StudioOutput[];
  category?: string;
}

/** Lista as composições compatíveis, já ordenadas. */
export function listCompositions(
  filtros: StudioCompositionFilters = {},
): StudioCompositionDefinition[] {
  return Array.from(composicoes.values())
    .filter((composicao) => {
      if (filtros.family && composicao.family !== filtros.family) return false;
      if (filtros.category && composicao.category !== filtros.category) return false;
      if (filtros.templateType && composicao.templateTypes?.length) {
        if (!composicao.templateTypes.includes(filtros.templateType)) return false;
      }
      if (filtros.outputs?.length) {
        return filtros.outputs.every((saida) => composicao.outputs.includes(saida));
      }
      return true;
    })
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || a.label.localeCompare(b.label, 'pt-BR'));
}

/**
 * Expande a composição em nós prontos para inserir.
 * Cada nó que ocupa um espaço recebe o papel semântico; os nós de primeiro
 * nível recebem o carimbo de origem.
 */
export function expandComposition(key: string): StudioCompositionNode[] {
  const composicao = composicoes.get(key);
  if (!composicao) return [];

  const porEspaco = new Map<string, StudioCompositionSlot>(
    composicao.slots.map((espaco) => [espaco.id, espaco]),
  );

  const carimbar = (no: StudioCompositionNode, raiz: boolean): StudioCompositionNode => {
    const espaco = no.slotId ? porEspaco.get(no.slotId) : undefined;
    return {
      ...no,
      ...(espaco ? { slotId: espaco.id, slotRole: espaco.role } : {}),
      ...(raiz
        ? {
            sourceComposition: composicao.key,
            sourceCompositionVersion: composicao.version,
          }
        : {}),
      ...(no.children ? { children: no.children.map((filho) => carimbar(filho, false)) } : {}),
    };
  };

  return composicao.build().map((no) => carimbar(no, true));
}

/** Confere se um bloco preenchido tem conteúdo de verdade no espaço. */
function espacoPreenchido(no: StudioBlockNode): boolean {
  // Bloco que agrupa outros (colunas, faixa) está preenchido quando tem peças
  // dentro dele — não tem texto próprio para conferir.
  if (no.children?.length) return true;

  const props = no.props ?? {};
  const candidatos = ['text', 'items', 'src', 'title', 'backgroundImage', 'formId'];
  return candidatos.some((chave) => {
    const valor = props[chave];
    return typeof valor === 'string' ? valor.trim().length > 0 : Boolean(valor);
  });
}

/** Problema encontrado na conferência de espaços. */
export interface StudioSlotIssue {
  message: string;
  blockId?: string;
}

/**
 * Confere os espaços obrigatórios das composições presentes no documento.
 * Só olha blocos que trazem o carimbo de origem — documentos montados à mão
 * seguem livres.
 */
export function checkCompositionSlots(blocos: StudioBlockNode[]): StudioSlotIssue[] {
  const problemas: StudioSlotIssue[] = [];

  const achatar = (nos: StudioBlockNode[]): StudioBlockNode[] =>
    nos.flatMap((no) => [no, ...achatar(no.children ?? [])]);

  // Uma composição pode ter nascido com mais de um bloco de primeiro nível
  // (ex.: chamada final + rodapé). A conferência é feita sobre o conjunto.
  const porComposicao = new Map<string, StudioBlockNode[]>();
  blocos.forEach((raiz) => {
    if (!raiz.sourceComposition) return;
    const grupo = porComposicao.get(raiz.sourceComposition) ?? [];
    grupo.push(raiz);
    porComposicao.set(raiz.sourceComposition, grupo);
  });

  porComposicao.forEach((raizes, chave) => {
    const composicao = composicoes.get(chave);
    if (!composicao) return;

    const arvore = achatar(raizes);
    composicao.slots
      .filter((espaco) => espaco.required)
      .forEach((espaco) => {
        const ocupante = arvore.find((no) => no.slotId === espaco.id);
        if (!ocupante) {
          problemas.push({
            message: `Em "${composicao.label}", o espaço ${espaco.label} foi removido e é obrigatório.`,
            blockId: raizes[0].id,
          });
          return;
        }
        if (!espacoPreenchido(ocupante)) {
          problemas.push({
            message: `Em "${composicao.label}", o espaço ${espaco.label} está vazio.`,
            blockId: ocupante.id,
          });
        }
      });
  });

  return problemas;
}

/**
 * Confere se a composição só usa blocos que existem e que atendem às saídas
 * declaradas. Usado nos testes e ao registrar a biblioteca.
 */
export function compositionUsesValidBlocks(composicao: StudioCompositionDefinition): boolean {
  const tipos = composicao.build().flatMap(function achatar(no): string[] {
    return [no.type, ...(no.children ?? []).flatMap(achatar)];
  });
  return tipos.every((tipo) => {
    const bloco = getBlock(tipo);
    if (!bloco) return false;
    return composicao.outputs.every((saida) => bloco.outputs.includes(saida));
  });
}
